import { db } from '../firebase.ts';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  getDocs,
  orderBy,
  limit,
  getDoc
} from 'firebase/firestore';

/**
 * Service for managing pronunciation tests in Firestore
 * Tests are stored in the 'pronunciation_tests' collection
 */

/**
 * Get the in-progress test for a user
 * @param {string} userId - The user ID
 * @returns {Promise<{id: string, data: object} | null>} Test document with ID or null if none found
 */
export const getInProgressTest = async (userId) => {
  try {
    const q = query(
      collection(db, 'pronunciation_tests'),
      where('userId', '==', userId),
      where('status', '==', 'in_progress'),
      limit(1)
    );

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return null;
    }

    const testDoc = querySnapshot.docs[0];
    return {
      id: testDoc.id,
      data: {
        ...testDoc.data(),
        // Convert Firestore timestamps to Date objects
        startedAt: testDoc.data().startedAt?.toDate(),
        completedAt: testDoc.data().completedAt?.toDate(),
        wordTests: testDoc.data().wordTests?.map(test => ({
          ...test,
          completedAt: test.completedAt?.toDate()
        }))
      }
    };
  } catch (error) {
    console.error('Error getting in-progress test:', error);
    return null;
  }
};

/**
 * Get the latest completed test for a user
 * @param {string} userId - The user ID
 * @returns {Promise<{id: string, data: object} | null>} Test document with ID or null if none found
 */
export const getLatestCompletedTest = async (userId) => {
  try {
    // Query without orderBy to avoid requiring composite index
    const q = query(
      collection(db, 'pronunciation_tests'),
      where('userId', '==', userId),
      where('status', '==', 'completed')
    );

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return null;
    }

    // Sort in JavaScript to get the latest test
    const tests = querySnapshot.docs.map(doc => ({
      id: doc.id,
      data: doc.data()
    }));

    // Sort by completedAt descending (most recent first)
    tests.sort((a, b) => {
      const dateA = a.data.completedAt?.toDate ? a.data.completedAt.toDate() : new Date(a.data.completedAt || 0);
      const dateB = b.data.completedAt?.toDate ? b.data.completedAt.toDate() : new Date(b.data.completedAt || 0);
      return dateB - dateA;
    });

    const latestTest = tests[0];

    return {
      id: latestTest.id,
      data: {
        ...latestTest.data,
        startedAt: latestTest.data.startedAt?.toDate ? latestTest.data.startedAt.toDate() : new Date(latestTest.data.startedAt),
        completedAt: latestTest.data.completedAt?.toDate ? latestTest.data.completedAt.toDate() : new Date(latestTest.data.completedAt),
        wordTests: latestTest.data.wordTests?.map(test => ({
          ...test,
          completedAt: test.completedAt instanceof Date ? test.completedAt : new Date(test.completedAt)
        }))
      }
    };
  } catch (error) {
    console.error('Error getting latest completed test:', error);
    return null;
  }
};

/**
 * Get all tests for a user (both in-progress and completed)
 * @param {string} userId - The user ID
 * @returns {Promise<Array<{id: string, data: object}>>} Array of test documents
 */
export const getAllUserTests = async (userId) => {
  try {
    const q = query(
      collection(db, 'pronunciation_tests'),
      where('userId', '==', userId),
      orderBy('startedAt', 'desc')
    );

    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      data: {
        ...doc.data(),
        startedAt: doc.data().startedAt?.toDate(),
        completedAt: doc.data().completedAt?.toDate(),
        wordTests: doc.data().wordTests?.map(test => ({
          ...test,
          completedAt: test.completedAt?.toDate()
        }))
      }
    }));
  } catch (error) {
    console.error('Error getting all user tests:', error);
    return [];
  }
};

/**
 * Create a new pronunciation test
 * @param {string} userId - The user ID
 * @param {object} childAge - Child age object with years, months, and decimal
 * @returns {Promise<string>} The ID of the created test document
 */
export const createNewTest = async (userId, childAge) => {
  try {
    const testData = {
      userId,
      status: 'in_progress',
      startedAt: new Date(),
      completedAt: null,
      childAge: {
        years: childAge.years || 0,
        months: childAge.months || 0,
        decimal: childAge.decimal || 0
      },
      wordTests: [],
      problemSounds: [],
      mildProblemSounds: [],
      severeProblemSounds: [],
      currentStep: 1,
      currentWordIndex: 0,
      selectedYears: childAge.years || 0,
      selectedMonths: childAge.months || 0
    };

    const docRef = await addDoc(collection(db, 'pronunciation_tests'), testData);
    return docRef.id;
  } catch (error) {
    console.error('Error creating new test:', error);
    throw error;
  }
};

/**
 * Update test progress (state for resuming)
 * @param {string} testId - The test document ID
 * @param {object} data - Data to update
 * @returns {Promise<void>}
 */
export const updateTestProgress = async (testId, data) => {
  try {
    const testRef = doc(db, 'pronunciation_tests', testId);
    await updateDoc(testRef, {
      ...data,
      updatedAt: new Date()
    });
  } catch (error) {
    console.error('Error updating test progress:', error);
    throw error;
  }
};

/**
 * Complete a test and save final results
 * @param {string} testId - The test document ID
 * @param {object} finalData - Final test data including problem sounds
 * @returns {Promise<void>}
 */
export const completeTest = async (testId, finalData) => {
  try {
    const testRef = doc(db, 'pronunciation_tests', testId);
    await updateDoc(testRef, {
      ...finalData,
      status: 'completed',
      completedAt: new Date(),
      updatedAt: new Date()
    });
  } catch (error) {
    console.error('Error completing test:', error);
    throw error;
  }
};

/**
 * Delete or abandon a test
 * @param {string} testId - The test document ID
 * @returns {Promise<void>}
 */
export const deleteTest = async (testId) => {
  try {
    const testRef = doc(db, 'pronunciation_tests', testId);
    await deleteDoc(testRef);
  } catch (error) {
    console.error('Error deleting test:', error);
    throw error;
  }
};

/**
 * Get a specific test by ID
 * @param {string} testId - The test document ID
 * @returns {Promise<{id: string, data: object} | null>}
 */
export const getTestById = async (testId) => {
  try {
    const testRef = doc(db, 'pronunciation_tests', testId);
    const testDoc = await getDoc(testRef);

    if (!testDoc.exists()) {
      return null;
    }

    return {
      id: testDoc.id,
      data: {
        ...testDoc.data(),
        startedAt: testDoc.data().startedAt?.toDate(),
        completedAt: testDoc.data().completedAt?.toDate(),
        wordTests: testDoc.data().wordTests?.map(test => ({
          ...test,
          completedAt: test.completedAt?.toDate()
        }))
      }
    };
  } catch (error) {
    console.error('Error getting test by ID:', error);
    return null;
  }
};

/**
 * Get problem sounds from the latest completed pronunciation test
 * @param {string} userId - The user ID
 * @returns {Promise<object>} Problem sounds object
 */
export const getProblemSounds = async (userId) => {
  try {
    // Get from pronunciation_tests collection
    const latestTest = await getLatestCompletedTest(userId);

    if (latestTest) {
      const problemSoundsData = {
        severeProblemSounds: latestTest.data.severeProblemSounds || [],
        mildProblemSounds: latestTest.data.mildProblemSounds || [],
        problemSounds: latestTest.data.problemSounds || []
      };
      return problemSoundsData;
    }

    return {
      severeProblemSounds: [],
      mildProblemSounds: [],
      problemSounds: []
    };
  } catch (error) {
    console.error('Error getting problem sounds:', error);
    return {
      severeProblemSounds: [],
      mildProblemSounds: [],
      problemSounds: []
    };
  }
};

/**
 * Get child age from the latest completed pronunciation test
 * @param {string} userId - The user ID
 * @returns {Promise<object|null>} Child age object with years, months, and decimal or null
 */
export const getChildAgeFromTest = async (userId) => {
  try {
    const latestTest = await getLatestCompletedTest(userId);

    if (latestTest && latestTest.data.childAge) {
      return latestTest.data.childAge;
    }

    return null;
  } catch (error) {
    console.error('Error getting child age from test:', error);
    return null;
  }
};
